import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSlideCopy } from '@/lib/i18n'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'
import { useLocale } from '@/lib/locale'
import type { SlideProps } from '@/components/SlideWizard'
import {
  FiCreditCard,
  FiHome,
  FiSmartphone,
  FiSend,
  FiGift,
  FiHash,
  FiChevronRight,
} from 'react-icons/fi'
import { copy, type AskSection, type MethodId } from './Slide4CallBank.copy'

const ease = [0.16, 1, 0.3, 1] as const

const METHOD_ICONS: Record<MethodId, typeof FiCreditCard> = {
  card: FiCreditCard,
  'bank-transfer': FiHome,
  'payment-app': FiSmartphone,
  wire: FiSend,
  'gift-card': FiGift,
  crypto: FiHash,
}

function isFlatAskList(v: string[] | AskSection[]): v is string[] {
  return v.length > 0 && typeof v[0] === 'string'
}

export default function Slide4CallBank(_props: SlideProps) {
  const c = useSlideCopy(copy)
  const firstCall = COMMUNITY_PARTNER?.verifyContact?.firstCall
  // The slide-over enters from the end edge of the reading direction.
  const rtl = useLocale() === 'ar'
  const offscreen = rtl ? '-100%' : '100%'
  const [openId, setOpenId] = useState<MethodId | null>(null)

  const openMethod = openId ? c.methods.find((m) => m.id === openId) ?? null : null
  const OpenIcon = openMethod ? METHOD_ICONS[openMethod.id] : null

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-3xl">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-xs sm:text-sm uppercase tracking-[0.25em] text-slate-400 font-semibold"
      >
        {c.eyebrow}
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25, ease }}
        className="mt-4 text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.55, ease }}
        className="mt-8 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-2xl"
      >
        {c.body}
      </motion.p>

      {/* Partner face (a credit union): name the member's own institution
          first, with its real number. English faces only. */}
      {firstCall && !rtl && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease }}
          className="mt-6 rounded-2xl bg-violet-50 px-5 py-4 text-lg leading-relaxed text-slate-800"
        >
          {firstCall.lead}
          <a href={`tel:${firstCall.tel}`} className="whitespace-nowrap font-semibold text-violet-700 underline underline-offset-2">
            {firstCall.phone}
          </a>
          {firstCall.after}
        </motion.p>
      )}

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.85, ease }}
        className="mt-10 text-sm uppercase tracking-[0.2em] text-slate-400 font-semibold"
      >
        {c.prompt}
      </motion.p>

      <div className="mt-4 grid w-full max-w-xl grid-cols-1 gap-2 sm:grid-cols-2">
        {c.methods.map((m, i) => {
          const Icon = METHOD_ICONS[m.id]
          return (
            <motion.button
              key={m.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.95 + i * 0.06, ease }}
              type="button"
              onClick={() => setOpenId(m.id)}
              className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-start transition-all hover:border-slate-300 hover:shadow-[0_2px_10px_rgba(15,23,42,0.04)] cursor-pointer"
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-600">
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex-1 text-base font-medium text-slate-900 leading-tight">
                {m.label}
              </span>
              <FiChevronRight className="dir-flip h-4 w-4 text-slate-300 transition-colors group-hover:text-slate-500" />
            </motion.button>
          )
        })}
      </div>

      <AnimatePresence>
        {openMethod && OpenIcon && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpenId(null)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white shadow-2xl overflow-y-auto text-start"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/90 backdrop-blur px-6 py-4">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                    <OpenIcon className="h-3.5 w-3.5" />
                  </span>
                  <h3 className="text-sm font-semibold text-slate-700">
                    {openMethod.label}
                  </h3>
                </div>
                <button
                  onClick={() => setOpenId(null)}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700 cursor-pointer"
                  aria-label={c.closeLabel}
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <p className="text-sm text-slate-600 leading-relaxed">
                  {openMethod.intro}
                </p>

                <h4 className="mt-7 text-sm font-semibold text-slate-900">
                  {c.whatToSayHeading}
                </h4>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed italic">
                  {openMethod.whatToSay}
                </p>

                {openMethod.askThemTo.length > 0 && (
                  isFlatAskList(openMethod.askThemTo) ? (
                    <>
                      <h4 className="mt-6 text-sm font-semibold text-slate-900">
                        {c.askThemToHeading}
                      </h4>
                      <ul className="mt-2 space-y-1.5 text-sm text-slate-600 leading-relaxed">
                        {openMethod.askThemTo.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-1.5 inline-flex h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    openMethod.askThemTo.map((section) => (
                      <div key={section.heading} className="mt-6">
                        <h4 className="text-sm font-semibold text-slate-900">
                          {section.heading}
                        </h4>
                        <ul className="mt-2 space-y-1.5 text-sm text-slate-600 leading-relaxed">
                          {section.items.map((item) => (
                            <li key={item} className="flex items-start gap-2">
                              <span className="mt-1.5 inline-flex h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )
                )}

                {openMethod.keepNearby.length > 0 && (
                  <>
                    <h4 className="mt-6 text-sm font-semibold text-slate-900">
                      {c.keepNearbyHeading}
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-sm text-slate-600 leading-relaxed">
                      {openMethod.keepNearby.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1.5 inline-flex h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <div className="mt-7 rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {openMethod.note}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
